import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useOrgName, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { useAuth } from '../../contexts/AuthContext';
import { Alert, Avatar, Button, Dialog, Drawer, ErrorState, EmptyState, MenuItem, MenuSeparator, Popover, Segmented, usePopover, useToast } from '../../components/shared';
import adminAPI, { errorMessage } from '../../services/adminApi';
import {
  INVITE_ROLE_ORDER,
  ROLES,
  childrenByParent,
  describeScope,
  roleDescription,
  formatDate,
  formatSignIn,
  isEmail,
  lower,
  nameFromEmail,
  parseEmails,
  plural,
  pluralOf,
  primaryRole,
  roleLabel,
  sortUsers,
} from './adminModel';
import { CardSearch } from './adminShared';
import { AgentPhotosCard } from './AgentPhotosCard';
import { consentReturnMessage } from './agentPhotosModel';
import { clearAgentPhotos } from '../../services/agentPhotos';
import { useAdminData } from './useAdminData';
import './admin.css';

const EMPTY_SCOPE = { mode: 'org', hierarchyNodeIds: [], communityIds: [], queueIds: [] };
const INVITE_SCOPE = { ...EMPTY_SCOPE, mode: 'tree' };

function scopeOf(user) {
  if (user.ownCallsOnly) return { ...EMPTY_SCOPE, mode: 'own' };
  if ((user.scopedQueueIds || []).length) return { ...EMPTY_SCOPE, mode: 'queues', queueIds: [...user.scopedQueueIds] };
  if ((user.scopedHierarchyNodeIds || []).length || (user.scopedCommunityIds || []).length)
    return { ...EMPTY_SCOPE, mode: 'tree', hierarchyNodeIds: [...(user.scopedHierarchyNodeIds || [])], communityIds: [...(user.scopedCommunityIds || [])] };
  return { ...EMPTY_SCOPE };
}

function scopeRequest(scope) {
  return {
    hierarchyNodeIds: scope.mode === 'tree' ? scope.hierarchyNodeIds : [],
    communityIds: scope.mode === 'tree' ? scope.communityIds : [],
    queueIds: scope.mode === 'queues' ? scope.queueIds : [],
    ownCallsOnly: scope.mode === 'own',
  };
}

const scopeIsComplete = (scope) =>
  scope.mode === 'org' || scope.mode === 'own' || (scope.mode === 'tree' && scope.hierarchyNodeIds.length + scope.communityIds.length > 0) || (scope.mode === 'queues' && scope.queueIds.length > 0);

export function AdminUsersPage() {
  const org = useOrgName();
  const toast = useToast();
  const { user: me, hasPermission } = useAuth();
  const terms = useTerms();
  const { data, loading, error, retry, reload } = useAdminData(
    {
      users: adminAPI.getUsers,
      levels: adminAPI.getLevels,
      nodes: adminAPI.getNodes,
      communities: adminAPI.getCommunities,
      entities: () => adminAPI.getRoutingEntities(90),
      agentPhotos: adminAPI.getAgentPhotos,
    },
    { optional: ['levels', 'nodes', 'communities', 'entities', 'agentPhotos'] }
  );
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(location.hash === '#invite');
  const [photoSettings, setPhotoSettings] = useState(null);
  const [photoReturn] = useState(() => consentReturnMessage(location.search));
  const [accessFor, setAccessFor] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [roleChange, setRoleChange] = useState(null);
  const rolePop = usePopover();
  const canManage = hasPermission('users:manage');
  const canSetRoles = hasPermission('users:manage_roles');
  const canManagePhotos = hasPermission('account:manage_settings');
  const agentPhotos = photoSettings || data?.agentPhotos || null;

  useEffect(() => {
    if (photoReturn && location.search) navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
  }, [photoReturn, navigate, location.pathname, location.hash, location.search]);

  const lookups = useMemo(() => {
    const levels = data?.levels || [];
    const levelName = new Map(levels.map((l) => [l.id, l.displayName]));
    return {
      levels,
      nodes: new Map((data?.nodes || []).map((n) => [n.id, { ...n, levelName: levelName.get(n.levelId) }])),
      communities: new Map((data?.communities || []).map((c) => [c.id, c.name])),
      communitiesList: (data?.communities || []).filter((c) => c.isActive !== false).sort((a, b) => a.name.localeCompare(b.name)),
      queues: new Map((data?.entities || []).filter((e) => e.routingEntityType === 'CallQueue').map((e) => [e.routingEntityId.toLowerCase(), e.routingEntityName])),
    };
  }, [data]);

  const queueLookups = useMemo(() => ({ ...lookups, queues: { get: (id) => lookups.queues.get(String(id || '').toLowerCase()) } }), [lookups]);

  const subtitle = `Who can sign in to Vertolytics for ${org}, what they can do, and which divisions or ${terms.communitiesLower} they see.`;
  const users = useMemo(() => {
    if (!data?.users) return [];
    return sortUsers(data.users);
  }, [data]);
  const q = query.trim().toLowerCase();
  const visible = users.filter(
    (u) => (!roleFilter || (u.roles || []).includes(roleFilter)) && (!q || u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
  );

  const exportColumns = [
    { key: 'displayName', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role', value: (u) => roleLabel(primaryRole(u.roles)) },
    { key: 'scope', header: 'Can see', value: (u) => describeScope(u, queueLookups, terms).text },
    { key: 'lastLogin', header: 'Last sign-in', value: (u) => (u.lastLogin ? new Date(u.lastLogin).toISOString() : '') },
    { key: 'invitedAt', header: 'Added', value: (u) => (u.invitedAt ? new Date(u.invitedAt).toISOString() : '') },
    { key: 'isActive', header: 'Active', value: (u) => (u.isActive ? 'Yes' : 'No') },
  ];

  const actions = (
    <Button variant="primary" onClick={() => setInviteOpen(true)} disabled={!canManage || !data}>
      <Icon name="plus" className="ico-sm" />
      Invite people
    </Button>
  );

  const page = (children) => (
    <Page title="Users and roles" subtitle={subtitle} actions={actions} exportConfig={{ name: 'Users', columns: exportColumns, rows: users }}>
      {children}
    </Page>
  );

  if (error) return page(<ErrorState title="Users couldn't load" body="Vertolytics couldn't read who has access. Nothing was changed." error={error} onRetry={retry} />);

  if (loading || !data) {
    return page(
      <div className="grid">
        <section className="card c-9 is-loading">
          <div className="card-body">
            <div style={{ height: 560 }} />
          </div>
        </section>
        <section className="card c-3 is-loading">
          <div className="card-body">
            <div style={{ height: 560 }} />
          </div>
        </section>
      </div>
    );
  }

  const changeRole = async (u, role) => {
    setRoleChange(null);
    try {
      await adminAPI.setUserRoles(u.id, [role]);
      await reload();
      toast(`Changed ${u.displayName}'s role to ${roleLabel(role)}`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    }
  };

  const setInitials = async (u, useInitials) => {
    try {
      await adminAPI.setPersonUsesInitials(u.id, useInitials);
      clearAgentPhotos();
      await reload();
      toast(useInitials ? `${u.displayName} shows initials instead of a photo` : `${u.displayName} shows their photo when they have one`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    }
  };

  const deactivate = async (u) => {
    setDeactivating(null);
    try {
      await adminAPI.deactivateUser(u.id);
      await reload();
      toast(`${u.displayName} can no longer sign in`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    }
  };

  const drawers = (
    <>
      <InviteDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        lookups={lookups}
        entities={data.entities || []}
        onDone={async (added) => {
          setInviteOpen(false);
          await reload();
          toast(`Added ${plural(added, 'person', 'people')}. Vertolytics doesn't send invitation emails, so share the sign-in link directly.`, 'mail');
        }}
      />
      <AccessDrawer
        user={accessFor}
        onClose={() => setAccessFor(null)}
        lookups={lookups}
        entities={data.entities || []}
        onDone={async (u) => {
          setAccessFor(null);
          await reload();
          toast(`Saved what ${u.displayName} can see`);
        }}
      />
      <Dialog
        open={Boolean(roleChange)}
        onClose={() => setRoleChange(null)}
        title="Change role"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setRoleChange(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => changeRole(roleChange.user, roleChange.role)}>
              Change role
            </button>
          </>
        }
      >
        <p className="small">
          Change {roleChange?.user.displayName}'s role from {roleLabel(primaryRole(roleChange?.user.roles))} to {roleLabel(roleChange?.role)}? What they can see and do changes at once.
        </p>
      </Dialog>
      <Dialog
        open={Boolean(deactivating)}
        onClose={() => setDeactivating(null)}
        title="Deactivate user"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDeactivating(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={() => deactivate(deactivating)}>
              Deactivate
            </button>
          </>
        }
      >
        <p className="small">
          Deactivate {deactivating?.displayName}? They can no longer sign in to Vertolytics. There is no reactivate option, and the same email address can't be added again.
        </p>
      </Dialog>
    </>
  );

  if (users.length <= 1) {
    return page(
      <>
        <EmptyState
          icon="users"
          title="Only you have access"
          body="Invite supervisors, managers, executives, and board members. They sign in with their Microsoft work account."
          actions={
            <Button variant="primary" onClick={() => setInviteOpen(true)} disabled={!canManage}>
              Invite people
            </Button>
          }
        />
        {drawers}
      </>
    );
  }

  return page(
    <>
      <div className="grid">
        <section className="card c-9">
          <div className="card-head" style={{ alignItems: 'center' }}>
            <CardSearch value={query} onChange={setQuery} placeholder="Search people" maxWidth={300} />
            <div className="acts">
              <Popover
                open={rolePop.open}
                onClose={rolePop.close}
                align="right"
                width={240}
                label="Filter by role"
                anchor={
                  <button type="button" className={`chip${roleFilter ? ' active' : ''}`} onClick={rolePop.toggle} aria-haspopup="true" aria-expanded={rolePop.open}>
                    <span className="k">Role</span>
                    {roleFilter ? roleLabel(roleFilter) : 'All'}
                    <Icon name="chevD" className="ico-sm" />
                  </button>
                }
              >
                <MenuItem
                  on={!roleFilter}
                  checkable
                  onClick={() => {
                    setRoleFilter(null);
                    rolePop.close();
                  }}
                >
                  All roles
                </MenuItem>
                {ROLES.filter((r) => r.id !== 'super_admin').map((r) => (
                  <MenuItem
                    key={r.id}
                    on={roleFilter === r.id}
                    checkable
                    onClick={() => {
                      setRoleFilter(r.id);
                      rolePop.close();
                    }}
                  >
                    {r.label}
                  </MenuItem>
                ))}
              </Popover>
            </div>
          </div>
          <div className="card-body flush">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Can see</th>
                    <th>Last sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      lookups={queueLookups}
                      isMe={Boolean(me && (me.id === u.id || lower(me.email) === lower(u.email)))}
                      canSetRoles={canSetRoles}
                      canManage={canManage}
                      photosAvailable={Boolean(agentPhotos?.available)}
                      canManagePhotos={canManagePhotos}
                      onInitials={(useInitials) => setInitials(u, useInitials)}
                      onRole={(role) => setRoleChange({ user: u, role })}
                      onAccess={() => setAccessFor(u)}
                      onDeactivate={() => setDeactivating(u)}
                    />
                  ))}
                </tbody>
              </table>
              {visible.length === 0 && (
                <p className="caption" style={{ padding: 16 }}>
                  No one matches.
                </p>
              )}
            </div>
          </div>
        </section>
        <div className="c-3 stack">
          <section className="card">
            <div className="card-head">
              <div>
                <div className="t">Roles</div>
                <div className="d">What each role can see and do</div>
              </div>
            </div>
            <div className="card-body flush">
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {ROLES.map((r) => (
                  <li key={r.id} style={{ padding: '10px 16px', borderTop: '1px solid var(--divider)' }}>
                    <div className="small strong">{r.label}</div>
                    <div className="caption">{roleDescription(r, org, terms)}</div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
          {agentPhotos && <AgentPhotosCard settings={agentPhotos} canManage={canManagePhotos} returnMessage={photoReturn} onChange={setPhotoSettings} />}
        </div>
      </div>
      {drawers}
    </>
  );
}

function UserRow({ user, lookups, isMe, canSetRoles, canManage, photosAvailable, canManagePhotos, onInitials, onRole, onAccess, onDeactivate }) {
  const pop = usePopover();
  const terms = useTerms();
  const scope = describeScope(user, lookups, terms);
  const role = primaryRole(user.roles);
  const roleOptions = ROLES.filter((r) => r.id !== 'super_admin' || role === 'super_admin');
  return (
    <tr>
      <td>
        <div className="row" style={{ gap: 10 }}>
          <Avatar name={user.displayName} photoId={user.isAgent ? user.entraId : undefined} />
          <div style={{ minWidth: 0 }}>
            <div className="cell-title">{user.displayName}</div>
            <div className="cell-sub user-email" title={user.email}>
              {user.email}
            </div>
          </div>
        </div>
      </td>
      <td>
        <select
          className="select"
          style={{ height: 30, minWidth: 170 }}
          aria-label={`Role for ${user.displayName}`}
          value={role}
          disabled={!canSetRoles || isMe || !user.isActive || role === 'super_admin'}
          title={isMe ? "You can't change your own role" : undefined}
          onChange={(e) => onRole(e.target.value)}
        >
          {roleOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <span className="row small nowrap" style={{ gap: 6 }}>
          <Icon name={scope.icon} className="ico-sm muted" />
          {scope.text}
        </span>
      </td>
      <td>
        {!user.isActive ? (
          <span className="badge">Deactivated</span>
        ) : user.lastLogin ? (
          <span className="small nowrap">{formatSignIn(user.lastLogin)}</span>
        ) : (
          <span className="badge b-info" title={`Added ${formatDate(user.invitedAt)}. No invitation email is sent; they haven't signed in yet.`}>
            <Icon name="mail" />
            Invited
          </span>
        )}
      </td>
      <td>
        <Popover
          open={pop.open}
          onClose={pop.close}
          align="right"
          width={220}
          label={`More for ${user.displayName}`}
          anchor={
            <button className="btn btn-ghost btn-sm icon-btn" type="button" aria-label={`More for ${user.displayName}`} onClick={pop.toggle} disabled={!canManage}>
              <Icon name="more" />
            </button>
          }
        >
          <MenuItem
            icon="eye"
            onClick={() => {
              pop.close();
              onAccess();
            }}
            disabled={!user.isActive}
          >
            Change what they can see
          </MenuItem>
          {photosAvailable && user.isAgent && (
            <MenuItem
              icon="user"
              checkable
              role="menuitemcheckbox"
              aria-checked={Boolean(user.useInitialsInsteadOfPhoto)}
              on={Boolean(user.useInitialsInsteadOfPhoto)}
              onClick={() => {
                pop.close();
                onInitials(!user.useInitialsInsteadOfPhoto);
              }}
              disabled={!canManagePhotos}
            >
              Use initials instead of photo
            </MenuItem>
          )}
          <MenuSeparator />
          <MenuItem
            icon="power"
            onClick={() => {
              pop.close();
              onDeactivate();
            }}
            disabled={isMe || !user.isActive}
          >
            Deactivate
          </MenuItem>
        </Popover>
      </td>
    </tr>
  );
}

function ScopeEditor({ role, scope, onChange, lookups, entities }) {
  const terms = useTerms();
  const levels = lookups.levels;
  const leafLevel = levels[levels.length - 1];
  const topPlural = lower(pluralOf(levels[0]));
  const options = [
    { value: 'org', label: 'Whole organization' },
    { value: 'tree', label: `Selected ${topPlural || 'groups'} or ${terms.communitiesLower}` },
  ];
  if (role === 'supervisor' || scope.mode === 'queues') options.push({ value: 'queues', label: 'Selected queues' });
  if (role === 'agent' || scope.mode === 'own') options.push({ value: 'own', label: 'Own calls only' });

  const nodes = [...lookups.nodes.values()];
  const kids = childrenByParent(nodes);
  const communitiesByNode = new Map();
  lookups.communitiesList.forEach((c) => {
    const key = c.hierarchyNodeId || '';
    if (!communitiesByNode.has(key)) communitiesByNode.set(key, []);
    communitiesByNode.get(key).push(c);
  });
  const toggle = (key, id, on) => onChange({ ...scope, [key]: on ? [...new Set([...scope[key], id])] : scope[key].filter((x) => x !== id) });
  const queues = entities.filter((e) => e.routingEntityType === 'CallQueue').sort((a, b) => a.routingEntityName.localeCompare(b.routingEntityName));

  const renderNode = (node, depth, coveredByParent) => {
    const checked = coveredByParent || scope.hierarchyNodeIds.includes(node.id);
    const isLeaf = node.levelId === leafLevel?.id;
    return (
      <React.Fragment key={node.id}>
        <label className="tree-row" style={{ fontWeight: 600, paddingLeft: 8 + depth * 22 }}>
          <input type="checkbox" checked={checked} disabled={coveredByParent} onChange={(e) => toggle('hierarchyNodeIds', node.id, e.target.checked)} />
          {node.name} <span className="cnt">{node.levelName}</span>
        </label>
        {isLeaf
          ? (communitiesByNode.get(node.id) || []).map((c) => (
              <label className="tree-row child" key={c.id} style={{ paddingLeft: 30 + depth * 22 }}>
                <input type="checkbox" checked={checked || scope.communityIds.includes(c.id)} disabled={checked} onChange={(e) => toggle('communityIds', c.id, e.target.checked)} />
                {c.name}
              </label>
            ))
          : (kids.get(node.id) || []).map((child) => renderNode(child, depth + 1, checked))}
      </React.Fragment>
    );
  };

  return (
    <div className="field">
      <span className="label">Can see</span>
      <Segmented options={options} value={scope.mode} onChange={(mode) => onChange({ ...scope, mode })} label="Can see" />
      {scope.mode === 'tree' && (
        <>
          <div className="panel tree" style={{ maxHeight: 260 }}>
            {(kids.get('') || []).map((node) => renderNode(node, 0, false))}
            {(communitiesByNode.get('') || []).map((c) => (
              <label className="tree-row" key={c.id}>
                <input type="checkbox" checked={scope.communityIds.includes(c.id)} onChange={(e) => toggle('communityIds', c.id, e.target.checked)} />
                {c.name}
              </label>
            ))}
          </div>
          <span className="hint">Selecting a {lower(levels[0]?.displayName || 'group')} includes its {terms.communitiesLower}, including ones added later.</span>
        </>
      )}
      {scope.mode === 'queues' && (
        <>
          <div className="panel tree" style={{ maxHeight: 260 }}>
            {queues.map((qe) => (
              <label className="tree-row" key={qe.routingEntityId}>
                <input type="checkbox" checked={scope.queueIds.includes(qe.routingEntityId)} onChange={(e) => toggle('queueIds', qe.routingEntityId, e.target.checked)} />
                {qe.routingEntityName}
              </label>
            ))}
            {queues.length === 0 && <div className="caption" style={{ padding: 8 }}>No call queues have synced yet.</div>}
          </div>
          <span className="hint">Supervisors see the calls, agents, and wallboard of these queues.</span>
        </>
      )}
      {scope.mode === 'own' && <span className="hint">Agents see only the calls they handled.</span>}
      {scope.mode === 'org' && <span className="hint">Every {lower(leafLevel?.displayName || 'group')} and {terms.communityLower}, including ones added later.</span>}
    </div>
  );
}

function InviteDrawer({ open, onClose, lookups, entities, onDone }) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState('supervisor');
  const [scope, setScope] = useState(INVITE_SCOPE);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  if (!open) return null;

  const list = parseEmails(emails);
  const invalid = list.filter((e) => !isEmail(e));

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    let added = 0;
    try {
      const request = scopeRequest(scope);
      for (const email of list) {
        // eslint-disable-next-line no-await-in-loop
        await adminAPI.createUser({
          email,
          displayName: nameFromEmail(email),
          roles: [role],
          scopedHierarchyNodeIds: request.hierarchyNodeIds,
          scopedCommunityIds: request.communityIds,
          scopedQueueIds: request.queueIds,
          ownCallsOnly: request.ownCallsOnly,
        });
        added += 1;
      }
      setEmails('');
      setScope(INVITE_SCOPE);
      await onDone(added);
    } catch (err) {
      setProblem(`${added ? `Added ${plural(added, 'person', 'people')}; ` : ''}${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Invite people"
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" type="button" onClick={submit} disabled={busy || list.length === 0 || invalid.length > 0 || !scopeIsComplete(scope)}>
            {busy ? 'Adding…' : list.length > 1 ? `Add ${list.length} people` : 'Add person'}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="label" htmlFor="ie">
            Work email addresses
          </label>
          <textarea className="textarea" id="ie" rows={2} value={emails} onChange={(e) => setEmails(e.target.value)} />
          {invalid.length ? (
            <span className="field-error">
              <Icon name="alert" className="ico-sm" />
              {invalid.join(', ')} {invalid.length === 1 ? "isn't an email address" : "aren't email addresses"}.
            </span>
          ) : (
            <span className="hint">Separate with commas. Board members can use their own email; they sign in with a Microsoft account.</span>
          )}
        </div>
        <div className="field">
          <label className="label" htmlFor="ir">
            Role
          </label>
          <select
            className="select"
            id="ir"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              if ((scope.mode === 'queues' && e.target.value !== 'supervisor') || (scope.mode === 'own' && e.target.value !== 'agent')) setScope(INVITE_SCOPE);
            }}
          >
            {INVITE_ROLE_ORDER.map((id) => (
              <option key={id} value={id}>
                {roleLabel(id)}
              </option>
            ))}
          </select>
        </div>
        <ScopeEditor role={role} scope={scope} onChange={setScope} lookups={lookups} entities={entities} />
        <Alert tone="info" icon="mail">
          Vertolytics doesn't send an invitation email. Share the sign-in link with them; they sign in with their Microsoft account.
        </Alert>
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    </Drawer>
  );
}

function AccessDrawer({ user, onClose, lookups, entities, onDone }) {
  const [scope, setScope] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (user) {
      setScope(scopeOf(user));
      setProblem(null);
    }
  }, [user]);

  if (!user || !scope) return null;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await adminAPI.setUserScope(user.id, scopeRequest(scope));
      await onDone(user);
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`What ${user.displayName} can see`}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" type="button" onClick={submit} disabled={busy || !scopeIsComplete(scope)}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small">
          {roleLabel(primaryRole(user.roles))} · {user.email}
        </p>
        <ScopeEditor role={primaryRole(user.roles)} scope={scope} onChange={setScope} lookups={lookups} entities={entities} />
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    </Drawer>
  );
}
